import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { CalendarDays, MapPin, Ticket, Loader2, ArrowLeft, Share2, Link2, Facebook, Twitter, MessageCircle, User, Armchair, Accessibility, Clock, Map as MapIcon, Sparkles, AlertTriangle, Navigation, Info, Lock } from "lucide-react";
import { eventsApi, checkoutApi, organisersApi, venuesApi, addonsApi, coverImageUrl, formatAuDate, centsToAUD, categoryLabel, tzFriendlyLabel } from "../lib/events";
import { extractError } from "../lib/api";
import { Logo } from "../components/Logo";
import VisualSeatMap from "../components/VisualSeatMap";
import VenueOverviewMap from "../components/VenueOverviewMap";
import { PricingLegendInline, PricingLegendDrawer } from "../components/PricingLegend";
import SectionQuickChips from "../components/SectionQuickChips";
import InlineSectionSeatPanel from "../components/InlineSectionSeatPanel";
import WaitlistForm from "../components/WaitlistForm";
import CrossSellCard from "../components/CrossSellCard";
import { trackCrossSellClick, trackWaitlistEmailArrival } from "../lib/crosssell";
import ChangeSectionConfirmDialog from "../components/ChangeSectionConfirmDialog";
import EventPolicies from "../components/EventPolicies";
import ExtrasPicker from "../components/ExtrasPicker";
import { toast } from "sonner";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

export default function PublicEvent({ mode = "public" }) {
  const params = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [qty, setQty] = useState({}); // { ticket_type_id: n }
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [related, setRelated] = useState([]);

  // Sprint F — reserved-seating state
  const [seats, setSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [activeSection, setActiveSection] = useState("");
  const [reservationInfo, setReservationInfo] = useState(null);
  const [reservationSecondsLeft, setReservationSecondsLeft] = useState(0);
  const seatSessionRef = useRef(null);

  // Sprint H — visual layout state
  const [venueLayout, setVenueLayout] = useState(null);
  const [renderModeOverride, setRenderModeOverride] = useState(null); // buyer a11y toggle

  // Sprint L — two-tier buyer map state
  const [sectionAvailability, setSectionAvailability] = useState([]);
  const [drillSection, setDrillSection] = useState(null); // null = overview view; string = section name being drilled

  // Sprint P-A1 — Change-section confirmation state (buyer has picks and taps
  // a different section on the map/chips)
  const [pendingSection, setPendingSection] = useState(null);
  const [changeConfirmBusy, setChangeConfirmBusy] = useState(false);
  const inlineSectionPanelRef = useRef(null);

  const isReserved = (event?.seating_mode || "general_admission") !== "general_admission";
  const hasEnded = !!event?.has_ended;
  const publicSlug = mode === "public" ? params.slug : null;
  const effectiveRenderMode = renderModeOverride
    || event?.render_mode
    || venueLayout?.render_mode
    || "list";

  // Load/persist a stable seat_session_id in sessionStorage (per browser tab)
  useEffect(() => {
    if (!isReserved) return;
    let sid = sessionStorage.getItem("gt-seat-session");
    if (!sid) {
      sid = `gt-seat-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("gt-seat-session", sid);
    }
    seatSessionRef.current = sid;
  }, [isReserved]);

  const refreshSeats = useCallback(async () => {
    if (!publicSlug) return;
    try {
      const rows = await venuesApi.publicSeats(publicSlug);
      setSeats(rows || []);
    } catch {
      /* silent — checkout will fail loudly */
    }
  }, [publicSlug]);

  const refreshSectionAvailability = useCallback(async () => {
    if (!publicSlug) return;
    try {
      const rows = await venuesApi.publicSectionAvailability(publicSlug);
      setSectionAvailability(rows || []);
    } catch {
      /* silent */
    }
  }, [publicSlug]);

  // Initial + periodic seat + section-availability refresh
  useEffect(() => {
    if (!isReserved || !publicSlug) return;
    refreshSeats();
    refreshSectionAvailability();
    const t = setInterval(() => {
      refreshSeats();
      refreshSectionAvailability();
    }, 20_000);
    return () => clearInterval(t);
  }, [isReserved, publicSlug, refreshSeats, refreshSectionAvailability]);

  // Sprint H — load venue layout (preset + viewport + render_mode) once per event
  useEffect(() => {
    if (!isReserved || !publicSlug) return;
    let alive = true;
    venuesApi
      .publicVenueLayout(publicSlug)
      .then((r) => alive && setVenueLayout(r))
      .catch(() => alive && setVenueLayout(null));
    return () => { alive = false; };
  }, [isReserved, publicSlug]);

  // Reservation TTL countdown (10-min from backend)
  useEffect(() => {
    if (!reservationInfo?.reserved_until) return;
    const tick = () => {
      const ms = new Date(reservationInfo.reserved_until).getTime() - Date.now();
      setReservationSecondsLeft(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [reservationInfo]);

  useEffect(() => {
    let alive = true;
    const fetcher = mode === "private"
      ? eventsApi.publicByKey(params.key)
      : eventsApi.publicBySlug(params.slug);
    fetcher
      .then((e) => alive && setEvent(e))
      .catch(() => alive && setError("This event isn't available."));
    return () => { alive = false; };
  }, [params.slug, params.key, mode]);

  // Load related events once the source event is loaded
  useEffect(() => {
    if (!event || mode !== "public") return;
    // Sprint AE — consent-safe arrival tracking from waitlist emails
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("via") === "waitlist_email" && qs.get("src")) {
        trackWaitlistEmailArrival(qs.get("src"), event.id);
      }
    } catch { /* tracking never blocks */ }
    let alive = true;
    eventsApi
      .publicRelated(event.slug, 3)
      .then((r) => alive && setRelated(r || []))
      .catch(() => {});
    return () => { alive = false; };
  }, [event, mode]);

  const totalTickets = useMemo(
    () => Object.values(qty).reduce((s, v) => s + (parseInt(v || 0, 10) || 0), 0),
    [qty],
  );
  const totalCents = useMemo(() => {
    if (!event) return 0;
    return (event.ticket_types || []).reduce((s, t) => s + (qty[t.id] || 0) * t.price_cents, 0);
  }, [qty, event]);

  // ---------------- Sprint T1 — Extras (add-ons) ----------------
  const [publicAddons, setPublicAddons] = useState([]);
  const [addonSel, setAddonSel] = useState({}); // {addon_id: {quantity, amount_cents}}
  useEffect(() => {
    if (!publicSlug || mode === "private") return;
    addonsApi
      .publicList(publicSlug)
      .then((rows) => setPublicAddons(rows || []))
      .catch(() => setPublicAddons([]));
  }, [publicSlug, mode]);

  const extrasTotalCents = useMemo(() => {
    return publicAddons.reduce((sum, a) => {
      const sel = addonSel[a.id];
      if (!sel || sel.quantity <= 0) return sum;
      const unit = a.pricing_type === "buyer_chooses" ? (sel.amount_cents || 0) : a.price_cents;
      return sum + unit * sel.quantity;
    }, 0);
  }, [publicAddons, addonSel]);

  const invalidExtra = useMemo(() => {
    return publicAddons.find((a) => {
      const sel = addonSel[a.id];
      return (
        sel && sel.quantity > 0 && a.pricing_type === "buyer_chooses" &&
        (sel.amount_cents == null || sel.amount_cents < a.min_amount_cents)
      );
    });
  }, [publicAddons, addonSel]);

  // Auto-clamp selected extras when ticket quantity drops (per_ticket scope)
  // or availability shrinks — keeps the client cart valid before server checks.
  useEffect(() => {
    setAddonSel((sel) => {
      let changed = false;
      const out = { ...sel };
      for (const a of publicAddons) {
        const s = out[a.id];
        if (!s || s.quantity <= 0) continue;
        let cap = a.max_per_order || 10;
        if (a.scope === "per_ticket") cap = Math.min(cap, totalTickets);
        if (a.available != null) cap = Math.min(cap, a.available);
        if (s.quantity > cap) {
          changed = true;
          if (cap <= 0) delete out[a.id];
          else out[a.id] = { ...s, quantity: cap };
        }
      }
      return changed ? out : sel;
    });
  }, [totalTickets, publicAddons]);

  const buildAddonItems = () =>
    publicAddons
      .filter((a) => addonSel[a.id]?.quantity > 0)
      .map((a) => ({
        addon_id: a.id,
        quantity: addonSel[a.id].quantity,
        ...(a.pricing_type === "buyer_chooses" ? { amount_cents: addonSel[a.id].amount_cents } : {}),
      }));

  // SEO — hook must be called unconditionally.
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const jsonLd = useMemo(() => {
    if (!event) return null;
    const tt = event.ticket_types || [];
    const offers = tt.map((t) => ({
      "@type": "Offer",
      "name": t.name,
      "price": (t.price_cents / 100).toFixed(2),
      "priceCurrency": "AUD",
      "availability": (t.available || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      "url": currentUrl,
    }));
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.title,
      "description": event.description || undefined,
      "startDate": event.start_at,
      "endDate": event.end_at || undefined,
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": event.venue_name,
        "address": { "@type": "PostalAddress", "streetAddress": event.venue_address, "addressRegion": event.state, "addressCountry": "AU" },
      },
      "image": coverImageUrl(event) || undefined,
      "offers": offers.length ? offers : undefined,
      "organizer": event.organiser_name ? { "@type": "Organization", "name": event.organiser_name } : undefined,
      "url": currentUrl,
    };
  }, [event, currentUrl]);

  useDocumentMeta({
    title: event ? `${event.title} · Getickets` : "Event · Getickets",
    description: event ? (event.description || `${event.title} on ${formatAuDate(event.start_at, event.timezone)} at ${event.venue_name}, ${event.state}.`) : undefined,
    image: event ? coverImageUrl(event) : undefined,
    url: currentUrl,
    type: "event",
    jsonLd,
  });

  const setQ = (ttId, v, max) => {
    const n = Math.max(0, Math.min(parseInt(v, 10) || 0, max));
    setQty((q) => ({ ...q, [ttId]: n }));
  };

  const onContinue = async () => {
    // Sprint AX — external booking events: fire-and-forget analytics + same-tab redirect. Never enter internal checkout.
    if (event?.booking_type === "external" && event?.external_booking_url) {
      const url = event.external_booking_url;
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/events/${event.slug}/external-click`,
              { method: "POST", keepalive: true }).catch(() => {});
      } catch { /* analytics must never block redirect */ }
      window.location.assign(url);
      return;
    }
    if (event?.booking_type === "external" && !event?.external_booking_url) {
      toast.error("Online booking is temporarily unavailable for this event.");
      return;
    }
    if (totalTickets === 0) return;
    // Sprint T1 — block checkout when a buyer_chooses extra is below minimum
    if (invalidExtra) {
      toast.error(`"${invalidExtra.name}" requires at least ${centsToAUD(invalidExtra.min_amount_cents)}.`);
      return;
    }
    // Sprint F — for reserved-seating events, buyer must have selected exactly
    // one seat per ticket. Reserve them via public endpoint before checkout.
    if (isReserved) {
      if (selectedSeats.length !== totalTickets) {
        toast.error(`Please pick ${totalTickets} seat${totalTickets === 1 ? "" : "s"} — you've selected ${selectedSeats.length}.`);
        return;
      }
    }
    setReserving(true);
    try {
      const items = Object.entries(qty)
        .filter(([, v]) => v > 0)
        .map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));

      const payload = { items };
      if (mode === "private") payload.event_private_key = params.key;
      else payload.event_slug = params.slug;
      // Sprint T1 — optional extras
      const addonItems = buildAddonItems();
      if (addonItems.length) payload.addon_items = addonItems;

      if (isReserved) {
        // Reserve the picked seats under a stable session id owned by this browser tab.
        if (!seatSessionRef.current) {
          seatSessionRef.current = `gt-seat-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
        }
        try {
          const res = await venuesApi.reserveSeats(params.slug, {
            session_id: seatSessionRef.current,
            seat_ids: selectedSeats,
          });
          setReservationInfo(res);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "One or more seats are no longer available. Please pick again.");
          setSelectedSeats([]);
          await refreshSeats();
          setReserving(false);
          return;
        }
        payload.event_seat_ids = selectedSeats;
        payload.seat_session_id = seatSessionRef.current;
      }

      const session = await checkoutApi.create(payload);
      navigate(`/checkout/${session.id}`);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setReserving(false);
    }
  };

  const toggleSeat = (seat) => {
    if (seat.status !== "available") return;
    setSelectedSeats((cur) => {
      if (cur.includes(seat.id)) return cur.filter((x) => x !== seat.id);
      if (cur.length >= totalTickets && totalTickets > 0) {
        toast.error(`You've picked ${totalTickets} seats. Increase ticket quantity to add more.`);
        return cur;
      }
      return [...cur, seat.id];
    });
  };

  // Sprint N-b4t — Best 4 Together auto-pick
  const [best4Busy, setBest4Busy] = useState(false);
  const findBest4Together = async () => {
    if (best4Busy) return;
    // Guardrail: buyer must have selected 4+ tickets first (avoids under/over picking)
    if (totalTickets < 4) {
      toast.error("Increase ticket quantity to 4 to use Best 4 Together.");
      return;
    }
    if (!seatSessionRef.current) {
      seatSessionRef.current = `gt-seat-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    }
    setBest4Busy(true);
    try {
      const res = await venuesApi.bestTogether(params.slug, {
        session_id: seatSessionRef.current,
        count: 4,
        allow_accessible: false,
      });
      // Merge picked seats into selection (replacing any prior picks so the
      // buyer sees exactly the 4 auto-picked seats — same UX as tapping them
      // one-by-one).
      setSelectedSeats(res.reserved_seat_ids || []);
      setReservationInfo({
        reserved_seat_ids: res.reserved_seat_ids,
        reserved_until: res.reserved_until,
      });
      // Sprint P-A1 — open the inline panel for the section Best 4 landed in
      if (res.section) {
        setDrillSection(res.section);
        setTimeout(() => {
          inlineSectionPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
      }
      await refreshSeats();
      toast.success(
        `Held 4 seats in ${res.section} · Row ${res.row_label}`,
      );
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || "No 4 seats together available. Try choosing seats manually.");
      } else {
        toast.error(extractError(err));
      }
      await refreshSeats();
    } finally {
      setBest4Busy(false);
    }
  };

  // Group reserved-seating seats by section then row for rendering
  const seatsBySection = useMemo(() => {
    if (!isReserved) return [];
    const bySec = new Map();
    for (const s of seats) {
      const sec = s.section || "General";
      if (!bySec.has(sec)) bySec.set(sec, new Map());
      const byRow = bySec.get(sec);
      const row = s.row_label || "";
      if (!byRow.has(row)) byRow.set(row, []);
      byRow.get(row).push(s);
    }
    return Array.from(bySec.entries()).map(([section, rowMap]) => ({
      section,
      rows: Array.from(rowMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([row, list]) => ({ row, seats: list.sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0)) })),
    }));
  }, [seats, isReserved]);

  // Once seats load, auto-pick the first section as the active tab
  useEffect(() => {
    if (isReserved && seatsBySection.length > 0 && !activeSection) {
      setActiveSection(seatsBySection[0].section);
    }
  }, [isReserved, seatsBySection, activeSection]);

  const seatCounts = useMemo(() => {
    if (!isReserved) return { available: 0, sold: 0, unavailable: 0 };
    let a = 0, s = 0, u = 0;
    for (const seat of seats) {
      if (seat.status === "available") a++;
      else if (seat.status === "sold") s++;
      else u++;
    }
    return { available: a, sold: s, unavailable: u };
  }, [seats, isReserved]);

  // Sprint L — two-tier overview map inputs
  const layoutSections = useMemo(() => venueLayout?.sections || [], [venueLayout]);
  const layoutZones = useMemo(() => venueLayout?.zones || [], [venueLayout]);
  const hasSectionGeometry = !!venueLayout?.has_section_geometry;
  const decorativeNames = useMemo(
    () => layoutSections.filter((s) => s.is_decorative).map((s) => s.name),
    [layoutSections],
  );
  // Two-tier mode requires: reserved seating + visual render + section geometry
  const twoTierMode =
    isReserved && effectiveRenderMode === "visual" && hasSectionGeometry;

  const selectedSectionCounts = useMemo(() => {
    if (!isReserved) return {};
    const idIndex = Object.fromEntries(seats.map((s) => [s.id, s]));
    const out = {};
    for (const id of selectedSeats) {
      const s = idIndex[id];
      if (!s) continue;
      const sec = s.section || "—";
      out[sec] = (out[sec] || 0) + 1;
    }
    return out;
  }, [selectedSeats, seats, isReserved]);

  // When user reserves, refresh availability so overview updates immediately
  useEffect(() => {
    if (reservationInfo) refreshSectionAvailability();
  }, [reservationInfo, refreshSectionAvailability]);

  // Sprint P-A1 — Section-select handler used by both the overview map and the
  // section chips. Same-section click is a no-op; different-section click while
  // seats are picked prompts a release-confirm dialog.
  const handleSectionSelect = useCallback((name) => {
    if (!name) return;
    if (drillSection === name) return;
    const picksInCurrent = (selectedSeats || []).filter((id) => {
      const s = seats.find((x) => x.id === id);
      return s && (s.section || "") === drillSection;
    });
    if (drillSection && picksInCurrent.length > 0) {
      // Ask for confirmation before releasing
      setPendingSection(name);
      return;
    }
    setDrillSection(name);
    // auto-scroll to the panel on next paint
    setTimeout(() => {
      inlineSectionPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, [drillSection, selectedSeats, seats]);

  const confirmSectionChange = useCallback(async () => {
    if (!pendingSection) return;
    setChangeConfirmBusy(true);
    try {
      // Release any prior seat reservation for this session
      if (isReserved && seatSessionRef.current) {
        try {
          await venuesApi.reserveSeats(params.slug, {
            session_id: seatSessionRef.current, seat_ids: [],
          });
        } catch { /* soft-fail — clearing selection below is what matters */ }
      }
      setSelectedSeats([]);
      setReservationInfo(null);
      setDrillSection(pendingSection);
      setPendingSection(null);
      await refreshSeats();
      await refreshSectionAvailability();
      setTimeout(() => {
        inlineSectionPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } finally {
      setChangeConfirmBusy(false);
    }
  }, [pendingSection, isReserved, params.slug, refreshSeats, refreshSectionAvailability]);

  const cancelSectionChange = useCallback(() => {
    setPendingSection(null);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--gt-bg-marketing)] flex flex-col" data-testid="public-event-not-found">
        <div className="p-6 sm:p-10">
          <Logo size="md" />
        </div>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="gt-label mb-3">404</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-3">Event not found.</h1>
            <p className="text-[var(--gt-ink-2)] text-sm mb-6">{error}</p>
            <Link to="/" className="gt-btn-primary">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="public-event-loading">
        <Loader2 size={20} className="animate-spin text-[var(--gt-ink-2)]" />
      </div>
    );
  }

  const isPaid = event.pricing_mode === "paid";
  // Sprint AX — external booking events swap the entire buy flow for a single redirect CTA
  const isExternal = event.booking_type === "external" && !!event.external_booking_url;
  const buyLabel = isExternal
    ? "Get Tickets"
    : totalTickets === 0
      ? "Choose tickets to continue"
      : "Continue to checkout";
  // Manual "close ticket sales" — event-level effective state from the resolver
  const salesClosed = !isExternal && !!event.effective_sales_closed;
  const salesClosedText = event.effective_sales_message
    || (event.effective_sales_label === "SOLD_OUT" ? "Sold out"
      : event.effective_sales_label === "COMING_SOON" ? "Coming soon" : "Sales closed");
  const scPaidBy = event.service_charge_paid_by || "customer";
  const gstRegistered = event.gst_registered_at_creation !== false && event.tax_enabled !== false;
  const gstOn = gstRegistered && event.gst_enabled !== false;
  const gstRateBps = event.gst_rate_bps != null ? event.gst_rate_bps : 1000;
  const scRateBps = event.service_charge_rate_bps != null ? event.service_charge_rate_bps : 250;
  const scFlatCents = event.service_charge_per_ticket_cents != null ? event.service_charge_per_ticket_cents : 30;
  const feeEstimate = (subtotalCents, qty) => {
    if (!subtotalCents) return { sc: 0, gst: 0, customerExtra: 0 };
    const sc = Math.round((subtotalCents * scRateBps) / 10000) + scFlatCents * qty;
    // Sprint AR — GST applies ONLY to the service fee, coupled to SC paid-by
    const gst = gstOn && sc > 0 ? Math.round((sc * gstRateBps) / 10000) : 0;
    return {
      sc,
      gst,
      customerExtra: scPaidBy === "customer" ? sc + gst : 0,
    };
  };
  const catLabel = categoryLabel(event.category);
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/s/${event.slug}`;
  const shareTitle = `${event.title} · ${formatAuDate(event.start_at)} · ${event.venue_name}`;

  const onShareCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Copy failed — please copy manually.");
    }
  };
  const onShareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
      } catch { /* user dismissed */ }
    } else {
      onShareCopy();
    }
  };
  const shareFacebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const shareTwitter = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
  const shareWhatsapp = `https://wa.me/?text=${encodeURIComponent(`${shareTitle} — ${shareUrl}`)}`;  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="public-event-page">
      <nav className="gt-glass-nav">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="gt-btn-ghost text-sm" data-testid="public-back-home">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <div className="relative">
        {coverImageUrl(event) ? (
          <div className="relative aspect-[21/9] sm:aspect-[16/6] bg-[var(--gt-ink)] overflow-hidden">
            <img
              src={coverImageUrl(event)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--gt-bg-marketing)] via-transparent to-transparent" />
          </div>
        ) : (
          <div className="h-24 bg-[var(--gt-ink)]" />
        )}
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-8 sm:pt-14 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {event.visibility === "private" && (
              <div className="gt-label mb-2 text-[var(--gt-accent)]">Private event</div>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {catLabel && (
                <span
                  className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-1 rounded-sm bg-[var(--gt-accent)]/10 text-[var(--gt-accent)] border border-[var(--gt-accent)]/30"
                  data-testid="public-event-category-badge"
                >
                  {catLabel}
                </span>
              )}
              {event.organiser_slug && event.organiser_name && (
                <Link
                  to={`/o/${event.organiser_slug}`}
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] underline underline-offset-4 decoration-[var(--gt-line)] hover:decoration-[var(--gt-ink)]"
                  data-testid="public-event-organiser-link"
                >
                  <User size={12} /> By {event.organiser_name}
                </Link>
              )}
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6"
              data-testid="public-event-title"
            >
              {event.title}
            </h1>

            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-8">
              <div className="flex items-start gap-2 text-sm">
                <CalendarDays size={16} className="mt-0.5 text-[var(--gt-accent)]" />
                <div>
                  <div className="gt-label mb-0.5">When</div>
                  <div data-testid="public-event-when">
                    {formatAuDate(event.start_at, event.timezone)}
                    {event.end_at ? ` — ${formatAuDate(event.end_at, event.timezone)}` : ""}
                  </div>
                  <div className="text-xs text-[var(--gt-ink-3)]" data-testid="public-event-timezone">{tzFriendlyLabel(event.timezone)}</div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <MapPin size={16} className="mt-0.5 text-[var(--gt-accent)]" />
                <div>
                  <div className="gt-label mb-0.5">Where</div>
                  <div className="font-semibold" data-testid="public-event-venue">{event.venue_name}</div>
                  <div className="text-xs text-[var(--gt-ink-2)]">{event.venue_address}, {event.state}</div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${event.venue_name}, ${event.venue_address}, ${event.state}, Australia`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-[var(--gt-accent)] hover:underline underline-offset-2"
                    data-testid="get-directions-link"
                  >
                    <Navigation size={11} aria-hidden="true" /> Get directions
                  </a>
                </div>
              </div>
            </div>

            {/* Sprint AB — Also playing on… (linked shows) */}
            {(event.other_shows || []).length > 0 && (
              <div className="mb-8" data-testid="also-playing-strip">
                <div className="gt-label mb-2">Also playing on…</div>
                <div className="flex flex-wrap gap-2">
                  {event.other_shows.map((s) => (
                    <Link
                      key={s.id}
                      to={`/e/${s.slug}`}
                      onClick={() => s.has_availability && trackCrossSellClick(event.id, s.id, "also_playing")}
                      className="inline-flex items-center gap-1.5 border border-[var(--gt-line)] rounded-sm px-3 py-1.5 text-xs font-semibold hover:border-[var(--gt-ink)] transition-colors"
                      data-testid={`also-playing-${s.id}`}
                    >
                      <CalendarDays size={12} className="text-[var(--gt-accent)]" />
                      {formatAuDate(s.start_at, s.timezone)}
                      {s.has_availability && (
                        <span
                          className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1 py-0.5"
                          data-testid={`also-playing-available-${s.id}`}
                        >
                          Tickets available
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {event.description && (
              <div className="prose max-w-none">
                <div className="gt-label mb-3">About this event</div>
                <p
                  className="text-[var(--gt-ink-2)] whitespace-pre-wrap leading-relaxed"
                  data-testid="public-event-description"
                >
                  {event.description}
                </p>
              </div>
            )}

            {/* Sprint F/L — Reserved-seating: two-tier overview or single-tier fallback */}
            {isReserved && !hasEnded && (() => {
              const seatsHaveCoords = seats.some(
                (s) => typeof s.x_percent === "number" && typeof s.y_percent === "number"
              );

              // Sprint L + P-A1: overview map ALWAYS visible in two-tier mode.
              // When the buyer picks a section, an inline seat panel opens BELOW
              // the map (no more full drill-down replacement) — matches the
              // JustEasyBook reference UX and keeps the venue context on screen.
              if (twoTierMode) {
                const filteredSeats = drillSection
                  ? seats.filter((s) => (s.section || "") === drillSection)
                  : [];
                const activeAvail = drillSection
                  ? sectionAvailability.find((a) => a.section === drillSection)
                  : null;
                const zonesById = Object.fromEntries((layoutZones || []).map((z) => [z.id, z]));
                const activeSectionMeta = drillSection
                  ? layoutSections.find((s) => s.name === drillSection)
                  : null;
                const activeZone = activeSectionMeta?.pricing_zone_id
                  ? zonesById[activeSectionMeta.pricing_zone_id]
                  : null;
                const picksInSection = drillSection
                  ? (selectedSeats || []).filter((id) => {
                      const s = seats.find((x) => x.id === id);
                      return s && (s.section || "") === drillSection;
                    })
                  : [];
                // Sprint R1 — strict validation: Continue only activates when
                // the GLOBAL selected seat count equals the selected quantity.
                const canCheckout = totalTickets > 0 && selectedSeats.length === totalTickets;
                // Sprint R1 — panel quantity strip is wired to the event's
                // single ticket type (reserved events use one tt; multi-tt
                // events fall back to the sidebar Tickets panel for quantity).
                const singleTT = (event.ticket_types || []).length === 1 ? event.ticket_types[0] : null;
                const singleTTCap = singleTT ? Math.min(20, singleTT.available || 0) : 0;
                const grandTotalCents = totalCents + feeEstimate(totalCents, totalTickets).customerExtra + extrasTotalCents;

                const scrollToPanel = () => {
                  inlineSectionPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                };

                return (
                  <div className="mt-10 border-t border-[var(--gt-line)] pt-8" data-testid="two-tier-overview">
                    <div className="flex items-baseline justify-between flex-wrap gap-3">
                      <div className="gt-label flex items-center gap-2">
                        <MapIcon size={12} /> Choose a section
                      </div>
                      <div className="flex items-center gap-2">
                        {totalTickets >= 4 && (
                          <button
                            type="button"
                            onClick={findBest4Together}
                            disabled={best4Busy}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-ink)] rounded-sm bg-[var(--gt-ink)] text-white hover:bg-black disabled:opacity-60"
                            data-testid="best-4-together-btn"
                            aria-label="Auto-pick 4 seats together in the best available section"
                          >
                            {best4Busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            Best 4 Together
                          </button>
                        )}
                        <div className="sm:hidden">
                          <PricingLegendDrawer zones={layoutZones} />
                        </div>
                        {event?.render_mode === "visual" && (
                          <button
                            type="button"
                            onClick={() => setRenderModeOverride("list")}
                            className="text-[10px] uppercase tracking-widest underline underline-offset-4 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)]"
                            data-testid="two-tier-switch-list-btn"
                          >
                            List view
                          </button>
                        )}
                      </div>
                    </div>

                    <SectionQuickChips
                      availability={sectionAvailability}
                      onSelectSection={handleSectionSelect}
                      activeSection={drillSection}
                      selectedSectionCounts={selectedSectionCounts}
                      decorativeNames={decorativeNames}
                    />

                    <VenueOverviewMap
                      sections={layoutSections}
                      zones={layoutZones}
                      availability={sectionAvailability}
                      onSelectSection={handleSectionSelect}
                      viewportWidth={venueLayout?.layout?.viewport_width || 100}
                      viewportHeight={venueLayout?.layout?.viewport_height || 100}
                      selectedSectionCounts={selectedSectionCounts}
                      activeSection={drillSection}
                    />

                    <div className="hidden sm:block">
                      <PricingLegendInline zones={layoutZones} />
                    </div>

                    {/* Sprint P-A1 — Inline section seat panel appears BELOW the overview */}
                    <div ref={inlineSectionPanelRef}>
                      {drillSection && (
                        <InlineSectionSeatPanel
                          sectionName={drillSection}
                          sectionAvailability={activeAvail}
                          eventSlug={event.slug}
                          waitlistEnabled={event.waitlist_enabled !== false && new Date(event.start_at) > new Date()}
                          otherShows={event.other_shows}
                          eventId={event.id}
                          zone={activeZone}
                          filteredSeats={filteredSeats}
                          layout={venueLayout?.layout || venueLayout}
                          selectedSeats={selectedSeats}
                          onToggleSeat={toggleSeat}
                          totalTickets={totalTickets}
                          selectedCount={selectedSeats.length}
                          maxQuantity={singleTTCap}
                          onSetQuantity={singleTT ? (n) => setQ(singleTT.id, n, singleTTCap) : null}
                          chipPriceCents={singleTT ? singleTT.price_cents : 0}
                          totalAmountCents={grandTotalCents}
                          onChangeSection={() => {
                            setDrillSection(null);
                            scrollToPanel();
                          }}
                          onBest4={findBest4Together}
                          best4Busy={best4Busy}
                          canContinueCheckout={canCheckout}
                          onContinueCheckout={() => {
                            const el = document.querySelector("[data-testid='checkout-continue-btn']")
                              || document.querySelector("[data-testid='cart-continue-btn']")
                              || document.querySelector("aside");
                            el?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          onRefresh={() => { refreshSeats(); refreshSectionAvailability(); }}
                        />
                      )}
                    </div>

                    <ChangeSectionConfirmDialog
                      open={!!pendingSection}
                      currentSection={drillSection}
                      targetSection={pendingSection}
                      pickCount={picksInSection.length}
                      onCancel={cancelSectionChange}
                      onConfirm={confirmSectionChange}
                      busy={changeConfirmBusy}
                    />
                  </div>
                );
              }

              // Fallback: Sprint H single-tier visual map when possible
              const useVisual = effectiveRenderMode === "visual" && seatsHaveCoords;
              if (useVisual) {
                return (
                  <div>
                    {totalTickets >= 4 && (
                      <div className="mt-8 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={findBest4Together}
                          disabled={best4Busy}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-ink)] rounded-sm bg-[var(--gt-ink)] text-white hover:bg-black disabled:opacity-60"
                          data-testid="best-4-together-btn-single"
                          aria-label="Auto-pick 4 seats together in the best available section"
                        >
                          {best4Busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                          Best 4 Together
                        </button>
                      </div>
                    )}
                    <VisualSeatMap
                      seats={seats}
                      layout={venueLayout?.layout || venueLayout}
                      selectedSeats={selectedSeats}
                      onToggleSeat={toggleSeat}
                      maxSelectable={totalTickets}
                      onSwitchToList={() => setRenderModeOverride("list")}
                    />
                  </div>
                );
              }
              return (
                <SeatPicker
                  seatsBySection={seatsBySection}
                  activeSection={activeSection}
                  onSectionChange={setActiveSection}
                  selectedSeats={selectedSeats}
                  onToggleSeat={toggleSeat}
                  totalTickets={totalTickets}
                  seatCounts={seatCounts}
                  onSwitchToVisual={
                    // Only offer the switch-back if the event actually has a visual layout available
                    seatsHaveCoords && event?.render_mode === "visual"
                      ? () => setRenderModeOverride("visual")
                      : null
                  }
                />
              );
            })()}

            {/* Sprint S — Terms / Refund / Disclaimer collapsibles */}
            <EventPolicies event={event} />

            {/* Share */}
            <div className="mt-10" data-testid="public-event-share">
              <div className="gt-label mb-3 inline-flex items-center gap-2">
                <Share2 size={12} /> Share this event
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onShareNative}
                  data-testid="share-native-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 bg-[var(--gt-ink)] text-white rounded-sm hover:opacity-90"
                >
                  <Share2 size={14} /> Share
                </button>
                <a
                  href={shareWhatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-whatsapp"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] hover:bg-white"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  href={shareFacebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-facebook"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] hover:bg-white"
                >
                  <Facebook size={14} /> Facebook
                </a>
                <a
                  href={shareTwitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-twitter"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] hover:bg-white"
                >
                  <Twitter size={14} /> X / Twitter
                </a>
                <button
                  type="button"
                  onClick={onShareCopy}
                  data-testid="share-copy-link"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] hover:bg-white"
                >
                  <Link2 size={14} /> Copy link
                </button>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 self-start">
            {hasEnded ? (
              <div className="gt-card-hard bg-white" data-testid="event-ended-notice">
                <div className="gt-label mb-4">Tickets</div>
                <h3 className="font-heading text-2xl font-black tracking-tight mb-2">
                  This event has ended
                </h3>
                <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed mb-5">
                  It ran on {formatAuDate(event.start_at, event.timezone)}. Tickets are no longer
                  available for this event.
                </p>
                <Link to="/events" className="gt-btn-primary w-full" data-testid="ended-browse-events-btn">
                  Browse upcoming events
                </Link>
              </div>
            ) : (
            <div className="gt-card-hard bg-white !p-0 overflow-hidden">
              <div className="bg-[var(--gt-ink)] text-white px-5 py-4" data-testid="select-tickets-header">
                <div className="font-heading font-bold text-lg leading-tight">Select tickets</div>
                <div className="text-xs text-white/75 mt-1">
                  {(() => {
                    const tz = event.timezone || "Australia/Sydney";
                    const d = new Date(event.start_at);
                    const day = d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: tz });
                    const t1 = d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: tz });
                    const t2 = event.end_at
                      ? new Date(event.end_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: tz })
                      : null;
                    return `${day} ${t1}${t2 ? ` - ${t2}` : ""}`;
                  })()}
                </div>
              </div>
              <div data-testid="public-ticket-types">
                {(event.booking_type === "external") && (
                  <div className="px-5 py-6 text-sm" data-testid="public-external-booking-panel">
                    <div className="gt-label mb-2">External booking</div>
                    {event.external_booking_url ? (
                      <p className="text-[var(--gt-ink-2)]">
                        You'll be redirected to {event.external_booking_provider || "the external ticketing site"} to complete your booking.
                      </p>
                    ) : (
                      <p className="text-[var(--gt-ink-2)]" data-testid="public-external-unavailable">
                        Online booking is temporarily unavailable for this event. Please check back shortly.
                      </p>
                    )}
                  </div>
                )}
                {(event.booking_type !== "external") && (event.ticket_types || []).map((t, idx) => {
                  const closedTier = t.effective_sales_closed === true || t.purchasable === false || t.sales_closed === true;
                  const soldOut = (t.available || 0) === 0 || closedTier;
                  const tierLabel = closedTier
                    ? (t.effective_sales_message
                        || (t.effective_sales_label === "COMING_SOON" ? "Coming soon"
                          : t.effective_sales_label === "SALES_CLOSED" ? "Sales closed" : "Sold out"))
                    : "Sold out";
                  const perOrderCap = Math.min(20, t.available || 0);
                  const value = qty[t.id] || 0;
                  return (
                    <div
                      key={t.id}
                      className={`flex items-center justify-between gap-3 px-5 py-4 border-b border-[var(--gt-line)] ${
                        idx % 2 === 1 ? "bg-[#f1f1ef]" : "bg-white"
                      }`}
                      data-testid={`public-tt-${t.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-sm sm:text-base leading-tight">{t.name}</div>
                        {!soldOut && (
                          <div className="text-[11px] text-[var(--gt-ink-3)]" data-testid={`public-tt-avail-${t.id}`}>
                            Available
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="font-mono text-sm font-semibold text-right min-w-[3.5rem]">
                          {t.price_cents === 0 ? "$0.00" : centsToAUD(t.price_cents)}
                        </div>
                        {soldOut ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-[var(--gt-ink)] text-white"
                            data-testid={`public-tt-avail-${t.id}`}
                          >
                            {closedTier && <Lock size={11} />}{tierLabel}
                          </span>
                        ) : (
                          <div
                            className="flex items-center border border-[var(--gt-line)] rounded-sm bg-white"
                            data-testid={`qty-picker-${t.id}`}
                          >
                            <button
                              type="button"
                              onClick={() => setQ(t.id, Math.max(0, value - 1), perOrderCap)}
                              disabled={value <= 0}
                              aria-label={`Decrease ${t.name} quantity`}
                              className="w-9 h-9 flex items-center justify-center text-lg font-bold text-[var(--gt-ink-2)] hover:bg-[var(--gt-paper)] disabled:opacity-30 transition-colors"
                              data-testid={`qty-minus-${t.id}`}
                            >
                              −
                            </button>
                            <span
                              className="w-9 text-center font-mono font-bold text-sm select-none"
                              data-testid={`qty-value-${t.id}`}
                              aria-live="polite"
                              aria-label={`${value} ${t.name} tickets selected`}
                            >
                              {value}
                            </span>
                            <button
                              type="button"
                              onClick={() => setQ(t.id, Math.min(perOrderCap, value + 1), perOrderCap)}
                              disabled={value >= perOrderCap}
                              aria-label={`Increase ${t.name} quantity`}
                              className="w-9 h-9 flex items-center justify-center text-lg font-bold text-[var(--gt-ink-2)] hover:bg-[var(--gt-paper)] disabled:opacity-30 transition-colors"
                              data-testid={`qty-plus-${t.id}`}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 pt-4 pb-5">

              {/* Sprint Y — sold-out event-level waitlist */}
              {event.waitlist_enabled !== false &&
                new Date(event.start_at) > new Date() &&
                (event.ticket_types || []).length > 0 &&
                (event.ticket_types || []).every((t) => (t.available || 0) === 0) && (
                  <div className="mt-4" data-testid="event-waitlist-panel">
                    <CrossSellCard otherShows={event.other_shows} sourceEventId={event.id} placement="event_panel" />
                    <WaitlistForm slug={event.slug} compact />
                  </div>
                )}

              {/* Sprint F/L — selected-seat summary + reservation TTL */}
              {isReserved && totalTickets > 0 && (
                <div
                  className="mt-4 border-t border-[var(--gt-line)] pt-4 text-xs"
                  data-testid="seat-selection-summary"
                >
                  <div className="gt-label mb-2 flex items-center gap-1.5">
                    <Armchair size={12} /> Seats picked ({selectedSeats.length}/{totalTickets})
                  </div>
                  {selectedSeats.length === 0 ? (
                    <p className="text-[var(--gt-ink-3)]">Pick your seats on the plan →</p>
                  ) : (
                    <ul
                      className="space-y-1"
                      data-testid="selected-seat-list"
                    >
                      {(() => {
                        const idIndex = Object.fromEntries(seats.map((x) => [x.id, x]));
                        return selectedSeats
                          .map((sid) => idIndex[sid])
                          .filter(Boolean)
                          .sort((a, b) => (a.section || "").localeCompare(b.section || "")
                            || (a.row_label || "").localeCompare(b.row_label || "")
                            || (a.seat_number || 0) - (b.seat_number || 0))
                          .map((s) => (
                            <li
                              key={s.id}
                              className="flex items-center justify-between text-[11px] gap-2"
                              data-testid={`selected-seat-line-${s.seat_label}`}
                            >
                              <span className="uppercase tracking-widest text-[var(--gt-ink-3)] shrink-0">
                                {s.section || "—"}
                              </span>
                              <span className="font-mono flex-1 text-right">
                                Row {s.row_label || "—"} · Seat {s.seat_number ?? s.seat_label}
                              </span>
                            </li>
                          ));
                      })()}
                    </ul>
                  )}
                  {/* Sprint R1 — explicit mismatch message (under- or over-picked) */}
                  {selectedSeats.length > 0 && selectedSeats.length !== totalTickets && (
                    <div
                      className="mt-2 flex items-start gap-1.5 text-[11px] font-semibold text-[var(--gt-accent)]"
                      data-testid="seat-qty-mismatch-msg"
                      role="status"
                    >
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                      <span>
                        {selectedSeats.length < totalTickets
                          ? `Pick ${totalTickets - selectedSeats.length} more seat${totalTickets - selectedSeats.length === 1 ? "" : "s"} to match your ${totalTickets} ticket${totalTickets === 1 ? "" : "s"}.`
                          : `You've picked ${selectedSeats.length} seats but only ${totalTickets} ticket${totalTickets === 1 ? "" : "s"} — remove ${selectedSeats.length - totalTickets} seat${selectedSeats.length - totalTickets === 1 ? "" : "s"} or increase quantity.`}
                      </span>
                    </div>
                  )}
                  {reservationInfo && reservationSecondsLeft > 0 && (
                    <div className="mt-3 flex items-center gap-1.5 text-[var(--gt-accent)]" data-testid="reservation-ttl">
                      <Clock size={12} />
                      <span>Seats held for {Math.floor(reservationSecondsLeft / 60)}:{String(reservationSecondsLeft % 60).padStart(2, "0")}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Sprint T1 — Extras step (appears once tickets are selected) */}
              {totalTickets > 0 && publicAddons.length > 0 && (
                <ExtrasPicker
                  addons={publicAddons}
                  selection={addonSel}
                  onChange={setAddonSel}
                  totalTickets={totalTickets}
                />
              )}

              <div className="mt-6 pt-4 border-t border-[var(--gt-line)]">
                {isPaid && totalTickets > 0 && (scPaidBy === "customer" ? (
                  <div className="mb-2 text-xs text-[var(--gt-ink-2)]" data-testid="public-service-charge-line">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        Service fee
                        <button
                          type="button"
                          onClick={() => setShowFeeInfo((v) => !v)}
                          aria-expanded={showFeeInfo}
                          aria-label="How is the service fee calculated?"
                          className="p-1.5 -my-1.5 text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)] transition-colors"
                          data-testid="service-fee-info-toggle"
                        >
                          <Info size={13} />
                        </button>
                      </span>
                      <span className="font-mono">{centsToAUD(feeEstimate(totalCents, totalTickets).sc)}</span>
                    </div>
                    {showFeeInfo && (
                      <div className="mt-1 text-[11px] text-[var(--gt-ink-3)]" data-testid="service-fee-info-detail">
                        Service fee is calculated as {scRateBps / 100}% + {centsToAUD(scFlatCents)} per ticket.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-2 text-xs text-[var(--gt-ink-2)]" data-testid="public-service-charge-line">
                    <span>Service fee</span>
                    <span>Included in ticket price</span>
                  </div>
                ))}
                {isPaid && totalTickets > 0 && (!gstOn ? (
                  <div className="flex items-center justify-between mb-2 text-xs text-[var(--gt-ink-2)]" data-testid="public-gst-line">
                    <span>Service fee GST</span>
                    <span>Not applied</span>
                  </div>
                ) : scPaidBy === "customer" ? (
                  <div className="flex items-center justify-between mb-2 text-xs text-[var(--gt-ink-2)]" data-testid="public-gst-line">
                    <span>Service fee GST</span>
                    <span className="font-mono">{centsToAUD(feeEstimate(totalCents, totalTickets).gst)}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-2 text-xs text-[var(--gt-ink-2)]" data-testid="public-gst-line">
                    <span>Service fee GST</span>
                    <span>Included in ticket price</span>
                  </div>
                ))}
                {extrasTotalCents > 0 && (
                  <div className="flex items-center justify-between mb-2 text-xs text-[var(--gt-ink-2)]" data-testid="public-extras-line">
                    <span>Extras</span>
                    <span className="font-mono">{centsToAUD(extrasTotalCents)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="gt-label">Cost to buyer (AUD)</div>
                  <div className="font-heading text-xl font-black tracking-tight" data-testid="public-total">
                    {(() => {
                      const t = totalCents + feeEstimate(totalCents, totalTickets).customerExtra + extrasTotalCents;
                      return t === 0 ? "A$0.00" : centsToAUD(t);
                    })()}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={salesClosed ? undefined : onContinue}
                aria-disabled={salesClosed ? true : undefined}
                tabIndex={salesClosed ? -1 : undefined}
                className={salesClosed
                  ? "w-full mt-6 flex items-center justify-center gap-2 py-3 font-heading font-bold border-2 border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white opacity-100 cursor-not-allowed"
                  : "gt-btn-primary w-full mt-6"}
                disabled={
                  salesClosed
                    ? true
                    : isExternal
                    ? false
                    : reserving || totalTickets === 0 || (isReserved && selectedSeats.length !== totalTickets)
                }
                data-testid="public-buy-btn"
              >
                {salesClosed ? <Lock size={15} /> : reserving ? <Loader2 size={14} className="animate-spin" /> : <Ticket size={16} />}
                {salesClosed
                  ? salesClosedText
                  : isExternal
                  ? "Get Tickets"
                  : isReserved && totalTickets > 0 && selectedSeats.length !== totalTickets
                  ? selectedSeats.length < totalTickets
                    ? `Pick ${totalTickets - selectedSeats.length} more seat${totalTickets - selectedSeats.length === 1 ? "" : "s"}`
                    : `Remove ${selectedSeats.length - totalTickets} seat${selectedSeats.length - totalTickets === 1 ? "" : "s"}`
                  : buyLabel}
              </button>
              {salesClosed && event.effective_sales_message && event.effective_sales_message !== salesClosedText && (
                <p className="mt-3 text-xs text-center text-[var(--gt-ink-2)]" data-testid="public-sales-closed-note">
                  {event.effective_sales_message}
                </p>
              )}
              {!salesClosed && (
              <p className="mt-3 text-[10px] text-center uppercase tracking-widest text-[var(--gt-ink-3)]">
                {isExternal
                  ? `You'll be redirected to ${event.external_booking_provider || "the external booking site"}`
                  : isPaid
                  ? `Stripe${event?.stripe_test_mode ? " · Test Mode" : " · Secure checkout"} · AUD · 10-min hold`
                  : "No account needed · 10-min hold"}
              </p>
              )}
              </div>
            </div>
            )}
          </aside>
        </div>

        {/* Related events */}
        {related.length > 0 && (
          <section
            className="mt-16 pt-10 border-t border-[var(--gt-line)]"
            aria-labelledby="related-heading"
            data-testid="related-events-section"
          >
            <h2 id="related-heading" className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-6">
              You might also like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => {
                const rc = coverImageUrl(r);
                const rcat = categoryLabel(r.category);
                const rtt = r.ticket_types || [];
                const rMin = rtt.filter((t) => (t.price_cents || 0) > 0).reduce((m, t) => m == null ? t.price_cents : Math.min(m, t.price_cents), null);
                const rIsFree = rtt.length > 0 && rMin == null;
                return (
                  <Link
                    key={r.id}
                    to={`/e/${r.slug}`}
                    data-testid={`related-event-card-${r.slug}`}
                    className="group flex flex-col border border-[var(--gt-line)] rounded-sm bg-white overflow-hidden hover:border-[var(--gt-ink)] transition-colors"
                  >
                    <div className="relative aspect-[16/9] bg-[var(--gt-bg-surface)] overflow-hidden">
                      {rc ? (
                        <img src={rc} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-[var(--gt-ink-3)]">
                          <CalendarDays size={36} strokeWidth={1.5} />
                        </div>
                      )}
                      {rcat && (
                        <span className="absolute top-3 left-3 inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm bg-[var(--gt-accent)]/10 text-[var(--gt-accent)] border border-[var(--gt-accent)]/30">
                          {rcat}
                        </span>
                      )}
                    </div>
                    <div className="p-4 flex flex-col gap-1.5 flex-1">
                      <h3 className="font-heading text-base font-bold tracking-tight leading-tight group-hover:text-[var(--gt-accent)] transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                      <div className="text-xs text-[var(--gt-ink-2)]">
                        {formatAuDate(r.start_at, r.timezone)} · {r.venue_name}, {r.state}
                      </div>
                      <div className="mt-1 text-xs font-mono uppercase tracking-widest text-[var(--gt-ink-3)]">
                        {rIsFree ? "Free" : rMin != null ? `From ${centsToAUD(rMin)}` : ""}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] mt-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Logo inverse size="md" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <ManageCookiesLink className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]" />
            <div className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">
              © {new Date().getFullYear()} Getickets · AUD · GST-Ready
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


// ------------------------------------------------------------------
// SeatPicker — mobile-first grid/list seat selector (Sprint F, P0)
// ------------------------------------------------------------------
function SeatPicker({
  seatsBySection,
  activeSection,
  onSectionChange,
  selectedSeats,
  onToggleSeat,
  totalTickets,
  seatCounts,
  onSwitchToVisual,
}) {
  const active = seatsBySection.find((s) => s.section === activeSection) || seatsBySection[0];

  if (seatsBySection.length === 0) {
    return (
      <div className="mt-10 border-t border-[var(--gt-line)] pt-8" data-testid="seat-picker-empty">
        <div className="gt-label mb-3 flex items-center gap-2">
          <Armchair size={12} /> Reserved seating
        </div>
        <div className="text-sm text-[var(--gt-ink-3)]">Loading seat plan…</div>
      </div>
    );
  }

  return (
    <div className="mt-10 border-t border-[var(--gt-line)] pt-8" data-testid="seat-picker">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div className="gt-label flex items-center gap-2">
          <Armchair size={12} /> Choose your seats
          {totalTickets > 0 && (
            <span className="ml-1 normal-case tracking-normal text-[var(--gt-ink-3)] text-xs font-normal">
              — pick {totalTickets}
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--gt-ink-3)] font-mono flex items-center gap-3" data-testid="seat-inventory-count">
          {onSwitchToVisual && (
            <button
              type="button"
              onClick={onSwitchToVisual}
              className="uppercase tracking-widest underline underline-offset-4 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)]"
              data-testid="switch-to-visual-btn"
            >
              Visual map
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-[var(--gt-ink-2)] mb-5">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-[var(--gt-line)] bg-white" /> Available
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[var(--gt-accent)]" /> Selected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[var(--gt-ink)]/20 border border-[var(--gt-ink)]/30" /> Taken
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Accessibility size={12} className="text-[var(--gt-accent)]" /> Accessible
        </span>
      </div>

      {/* Section tabs (only when multi-section) */}
      {seatsBySection.length > 1 && (
        <div
          className="flex flex-wrap gap-1.5 mb-5 border-b border-[var(--gt-line)]"
          role="tablist"
          data-testid="seat-section-tabs"
        >
          {seatsBySection.map((sec) => {
            const isActive = active && sec.section === active.section;
            return (
              <button
                key={sec.section}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onSectionChange(sec.section)}
                data-testid={`seat-section-tab-${sec.section}`}
                className={
                  "px-3 py-2 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors " +
                  (isActive
                    ? "border-[var(--gt-ink)] text-[var(--gt-ink)]"
                    : "border-transparent text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]")
                }
              >
                {sec.section}
              </button>
            );
          })}
        </div>
      )}

      {/* Stage / screen indicator */}
      <div className="mx-auto max-w-md mb-6 text-center">
        <div className="h-1 bg-gradient-to-r from-transparent via-[var(--gt-ink)] to-transparent rounded" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gt-ink-3)] mt-1">Stage</div>
      </div>

      {/* Seat grid, grouped by row */}
      <div
        className="space-y-2 overflow-x-auto pb-2"
        data-testid={`seat-section-${active?.section || "n/a"}`}
      >
        {active?.rows.map((row) => (
          <div key={row.row} className="flex items-center gap-2 min-w-max">
            <div
              className="w-6 shrink-0 font-mono text-xs text-[var(--gt-ink-3)] text-right"
              aria-hidden="true"
            >
              {row.row}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {row.seats.map((seat) => {
                const isSelected = selectedSeats.includes(seat.id);
                const isAvailable = seat.status === "available";
                const isSold = seat.status === "sold";
                const cls = isSelected
                  ? "bg-[var(--gt-accent)] text-white border-[var(--gt-accent)]"
                  : isAvailable
                  ? "bg-white border-[var(--gt-line)] hover:border-[var(--gt-ink)] hover:bg-[var(--gt-bg-surface)]"
                  : isSold
                  ? "bg-[var(--gt-ink)]/10 border-[var(--gt-ink)]/20 text-[var(--gt-ink-3)] line-through cursor-not-allowed"
                  : "bg-[var(--gt-ink)]/5 border-[var(--gt-line)] text-[var(--gt-ink-3)] cursor-not-allowed";
                return (
                  <button
                    key={seat.id}
                    type="button"
                    onClick={() => onToggleSeat(seat)}
                    disabled={!isAvailable && !isSelected}
                    aria-pressed={isSelected}
                    aria-label={`Seat ${seat.seat_label}${seat.is_accessible ? " (accessible)" : ""}, ${seat.status}`}
                    data-testid={`seat-${seat.seat_label}`}
                    data-status={seat.status}
                    className={
                      "relative w-9 h-9 rounded-sm border text-[11px] font-mono font-semibold transition-colors flex items-center justify-center " +
                      cls
                    }
                  >
                    {seat.seat_number ?? seat.seat_label}
                    {seat.is_accessible && (
                      <Accessibility
                        size={9}
                        className={
                          "absolute -top-1 -right-1 rounded-full p-[1px] " +
                          (isSelected ? "bg-white text-[var(--gt-accent)]" : "bg-[var(--gt-accent)] text-white")
                        }
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
